<template>
	<div class="widget-editor height-100 display-flex flex-column" :class="{ 'has-file-preview': isFileViewerActive }" v-if="widget">
		<div class="width-100" v-if="showHeader">
			<BlockEditorWidgetHeader :miniAppType="miniAppType" />
		</div>
		<div class="display-flex flex-stretch-to-scroll">
			<div
				@click="sideBarFocus = 'form'"
				:class="{ 'tile-editor': isTileEditorMode }"
				class="widget-form-container flex-space-around--start flex-7 height-100"
			>
				<div class="display-flex flex-wrap widget-editor-container">
					<WidgetFormHeader
						v-on="$listeners"
						class="flex-col-12 mt-5 mb-2"
						:isMiniApp="isMiniApp"
						:miniAppType="miniAppType"
						:sideBarFocus="sideBarFocus"
						@show-widget-menu="onShowWidgetMenu"
						@focus-on-title="changeSideBarFocus('title')"
						@focus-on-page-options="changeSideBarFocus('options')"
					/>
					<div class="flex-col-12">
						<BlockEditorWidgetLayout
							ref="block-editor-widget"
							v-on="$listeners"
							:isMiniApp="isMiniApp"
							:hideBtnSection="hideBtnSection"
							:testResponse="testResponse"
							:sideBarFocus="sideBarFocus"
							:previousSteps="previousSteps"
							:hasEditPermissions="hasEditPermissions"
							:replaceInsertDataStringDict="replaceInsertDataStringDict"
							:configurationsMappingBlocksUUIDs="configurationsMappingBlocksUUIDs"
							:showConfigurationOnlyFields="showConfigurationOnlyFields"
							@focus-on-title="changeSideBarFocus('title')"
							@clear-sidebar-focus="changeSideBarFocus('form')"
						/>
						<FileViewerIndication
							:isActive="isFileViewerActive"
							:isFocused="sideBarFocus === 'pdf'"
							@focus-on-pdf="changeSideBarFocus('pdf')"
						/>
					</div>
				</div>
			</div>
			<div v-if="hasEditPermissions" class="widget-right-bar is-open">
				<WidgetSideBarSettings :previousSteps="previousSteps" @remove-title="changeSideBarFocus('form')" :sideBarFocus="sideBarFocus" />
			</div>
		</div>
	</div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

import BlockEditorWidgetHeader from './BlockEditorWidgetHeader';
import BlockEditorWidgetLayout from './BlockEditorWidgetLayout';
import FileViewerIndication from './FieldSettings/WidgetSettings/FileViewerIndication';
import WidgetFormHeader from './WidgetFormHeader';
import WidgetSideBarSettings from './WidgetSideBarSettings';
import { flatPreviousSteps } from '@/store/modules/selectedNode/getters';
import { replaceInsertDataStringDictionary, editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { setEditField } from '@/store/modules/widgetEditor/actions';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WidgetService from '@/utils/WidgetService';
import WorkflowService from '@/utils/WorkflowService';
export default {
	name: 'WidgetEditor',
	props: {
		testResponse: { type: Object },
		showHeader: { type: Boolean, default: true },
		hasEditPermissions: { type: Boolean },
		showConfigurationOnlyFields: { type: Boolean, default: false },
	},
	components: {
		WidgetFormHeader,
		FileViewerIndication,
		WidgetSideBarSettings,
		BlockEditorWidgetHeader,
		BlockEditorWidgetLayout,
	},
	computed: {
		...mapGettersShorthand({ editWorkflow }),
		...mapGetters('selectedNode', ['editNode', 'isMiniApp']),
		...mapGetters('widgetEditor', ['widget', 'editField', 'widgetMetadata', 'widgetFields']),
		...mapGetters([flatPreviousSteps.fullPath, replaceInsertDataStringDictionary.fullPath, configurationsMappingBlocksUUIDs.fullPath]),
		hideBtnSection() {
			// returns true if it's a mini app that isn't in shouldShowActionSection array
			return this.isMiniApp && !WidgetService.shouldShowActionSection(this.miniAppType);
		},
		isTileEditorMode() {
			return WorkflowService.isOfTypeTile(this.editWorkflow);
		},
		miniAppType() {
			if (this.isMiniApp) {
				return this.editNode.params_map.mini_app_type;
			}
			return null;
		},
		isFileViewerActive() {
			return ((this.widgetMetadata.options || {}).file_viewer || {}).active;
		},
		previousSteps() {
			return this[flatPreviousSteps.fullPath];
		},
		replaceInsertDataStringDict() {
			return this[replaceInsertDataStringDictionary.fullPath];
		},
		configurationsMappingBlocksUUIDs() {
			return this[configurationsMappingBlocksUUIDs.fullPath];
		},
	},
	methods: {
		...mapActions([setEditField.fullPath]),
		onShowWidgetMenu() {
			this.$refs['block-editor-widget'].showMenuDropDown();
		},
		changeSideBarFocus(newFocus) {
			this.sideBarFocus = newFocus;
			this.clearEditedField();
		},
		clearEditedField() {
			this[setEditField.fullPath](null);
		},
	},
	data() {
		return {
			sideBarFocus: 'form',
		};
	},
};
</script>

<style scoped lang="scss">
$top-bar-height: 69px;
$bottom-bar-height: 65px;

.widget-editor {
	background: #f5f8fe;

	.widget-form-container {
		height: 100%;
		padding: 0px 39px 120px 39px;
		.widget-editor-container {
			max-width: var(--widget-form-width);
		}

		&.tile-editor {
			padding: 0px 15px 120px 15px;
			::v-deep {
				.title-container {
					height: 60px !important;
					padding: 7px 10px;
					margin-bottom: 0px;
				}
				.default-widget-field .default-widget-field-title {
					font-size: 13px !important;
					color: #262626 !important;
					font-weight: normal !important;
				}
				.widget-title-override {
					.v-input input {
						font-size: 14px !important;
					}
				}
				.block-editor-widget-layout-new {
					transition: 0.3s;
					border-radius: 2px !important;
				}
			}
		}
		background: var(--primary-bg-color);
		z-index: 1;

		::v-deep .block-editor-widget-layout-new {
			transition: 0.3s;
		}
	}

	.widget-right-bar {
		z-index: 2;
		max-width: var(--widget-field-settings-editor);
		min-width: var(--widget-field-settings-editor);
		position: relative;
		border-left: 1px solid #e7e7ed;
		background: var(--bg-color) !important;
	}

	&.has-file-preview {
		::v-deep .block-editor-widget-layout-new {
			width: 430px;
			transition: 0.3s;
		}

		.widget-right-bar {
			&.is-open {
				width: 100%;
				opacity: 1;
				transition: 0.6s;
				transition: opacity 0.2s;
			}
			&:not(.is-open) {
				flex: 0;
				width: 0;
				opacity: 0;
				transition: opacity 0.2s;
				transition: 0.6s;
			}
		}
	}
}
</style>
